import { type FormEvent, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { brand } from '../../config/brand';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { clearReferral, pendingReferral } from './JoinPage';
import { ProviderSignIn } from './ProviderSignIn';
import { TurnstileBox, useTurnstile } from './Turnstile';
import { useFormErrors } from './useFormErrors';
import { useDocumentTitle } from '../../lib/title';

export function RegisterPage() {
  useDocumentTitle('Create an account');

  const errors = useFormErrors();

  const [form, setForm] = useState({ email: '', username: '', password: '', display_name: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  /*
   * Agreeing to the terms, which is a separate act from filling in a form.
   *
   * Its own state rather than a field on `form`, because it is not a value the
   * account has - it is a thing somebody did, and the server records when. See
   * `RegisterAction`.
   */
  const [accepted, setAccepted] = useState(false);
  const human = useTurnstile('register');

  /*
   * Who sent them, from the link they followed or from the one they followed a
   * week ago. Read once, on the render that draws the form, so the note below
   * cannot change under somebody mid-typing.
   */
  const { search } = useLocation();
  const [referrer] = useState(() => pendingReferral(search));

  function update(key: keyof typeof form) {
    return (event: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: event.target.value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    errors.clear();
    setBusy(true);

    try {
      await api.post(
        '/auth/register',
        { ...form, accept_terms: accepted, ref: referrer, turnstile: human.token },
        { anonymous: true },
      );

      /*
       * Forgotten on success only. A registration that failed on a taken
       * username is about to be tried again, and the person who sent them here
       * should not lose the credit to a typo.
       */
      clearReferral();
      setDone(true);
    } catch (error) {
      errors.capture(error);
      // A token is spent by the attempt, refused or not; the next needs a fresh one.
      human.reset();
    } finally {
      setBusy(false);
    }
  }

  /*
   * The same screen whether or not the address was already registered.
   *
   * The server returns an identical response for both (see RegisterAction), and
   * the client has to preserve that - branching here on anything would put the
   * enumeration oracle back after the API went to some trouble to remove it.
   *
   * It also means registration does not sign anybody in. They confirm the
   * address, then log in.
   */
  if (done) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`We have sent a confirmation link to ${form.email}.`}
        footer={
          <>
            Already confirmed? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
          </>
        }
      >
        <Alert tone="info">
          The link is good for 24 hours. If it does not arrive within a few minutes, check your spam folder.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${brand.name}`}
      subtitle="Upload your tracks and find people making things you like."
      stage={{
        headline: 'Everybody here has a face.',
        pitch:
          'Yours is one of them the moment you join, built rather than uploaded, and it is how a room sees you when you turn up to listen.',
      }}
      footer={
        <>
          Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
        </>
      }
    >
      {/* One click, or the four fields below it. */}
      <ProviderSignIn />

      <form onSubmit={submit} className="flex flex-col gap-4">
        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        <Field
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          required
          value={form.email}
          onChange={update('email')}
          error={errors.fields.email}
        />

        <Field
          label="Username"
          name="username"
          autoComplete="username"
          required
          value={form.username}
          onChange={update('username')}
          hint="Letters, numbers, underscore or hyphen. This is your profile address."
          error={errors.fields.username}
        />

        <Field
          label="Display name"
          name="display_name"
          autoComplete="nickname"
          value={form.display_name}
          onChange={update('display_name')}
          hint="Optional. Defaults to your username."
          error={errors.fields.display_name}
        />

        <Field
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={update('password')}
          // Length is the only rule the server enforces, so it is the only rule
          // stated here. Hinting at symbols people do not need is how you get
          // Password1! instead of a passphrase.
          hint="At least 10 characters. A short phrase beats a complicated word."
          error={errors.fields.password}
        />

        {/*
          Unticked to begin with, and the button will not go without it.

          Not pre-ticked, and not "by continuing you agree": both are ways of
          collecting an agreement from somebody who never gave one, and the
          record kept against the account would be worth exactly as much as the
          gesture that produced it.
        */}
        {/*
          Said out loud, because somebody typing their details into a form
          deserves to know a name is being attached to it - and because it is
          the friendliest thing on the page.
        */}
        {referrer !== null && (
          <p className="text-[13px] leading-relaxed text-soft" data-invited-by={referrer}>
            Invited by{' '}
            <Link to={`/${referrer}`} target="_blank" className="text-accent hover:underline">
              @{referrer}
            </Link>
            . They get credit once you confirm your email.
          </p>
        )}

        <label className="flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-soft">
          <input
            type="checkbox"
            checked={accepted}
            data-accept-terms
            onChange={(event) => setAccepted(event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-accent hover:underline">
              Terms of Service
            </Link>{' '}
            and the{' '}
            <Link to="/privacy" target="_blank" className="text-accent hover:underline">
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        {errors.fields.accept_terms !== undefined && (
          <p className="text-[13px] text-negative">{errors.fields.accept_terms}</p>
        )}

        <TurnstileBox state={human} />

        <Button type="submit" loading={busy} full disabled={!accepted || !human.ready}>
          {copy['cta.signup']}
        </Button>
      </form>
    </AuthLayout>
  );
}
